import { navigateTo, useCookie, useLocalePath, useNuxtApp } from '#imports';
import {
  clearApiCache,
  useClientApiFetch
} from '~/composables/useLocalizedFetch';

const PUBLIC_AUTH_COOKIE = 'idmanify_user';
const AUTH_SYNC_KEY = 'idmanify_auth_sync';

export const useAuth = () => {
  try {
    return useNuxtApp().$auth;
  } catch {
    return globalThis.__IDMANIFY_AUTH__;
  }
};

/**
 * Web auth policy:
 * Laravel Sanctum's session cookie is the only credential for the Nuxt web app.
 * This composable never persists Bearer/API tokens in JS-readable storage.
 * `idmanify_user` is only a sanitized UI mirror for reactivity and legacy code.
 */
const sanitizeAuthUser = (user) => {
  if (!user || typeof user !== 'object') return null;

  const safeUser = { ...user };
  delete safeUser.token;
  delete safeUser.access_token;
  delete safeUser.plain_text_token;

  return safeUser?.email ? safeUser : null;
};

const publicAuthCookie = () => {
  try {
    return useCookie(PUBLIC_AUTH_COOKIE, {
      path: '/',
      sameSite: 'strict',
      maxAge: 60 * 60 * 24 * 365
    });
  } catch {
    return {
      get value() {
        return null;
      },
      set value(user) {
        if (!import.meta.client) return;
        if (!user) {
          document.cookie = `${PUBLIC_AUTH_COOKIE}=; Max-Age=0; path=/; SameSite=Strict`;
          return;
        }
        document.cookie = `${PUBLIC_AUTH_COOKIE}=${encodeURIComponent(JSON.stringify(user))}; Max-Age=${60 * 60 * 24 * 365}; path=/; SameSite=Strict`;
      }
    };
  }
};

const persistPublicAuthUser = (user, auth, cookie) => {
  const safeUser = sanitizeAuthUser(user);
  cookie.value = safeUser;
  auth.session.value = safeUser;
  return safeUser;
};

const clearLocalAuthState = (auth, cookie, nuxtApp) => {
  cookie.value = null;
  auth.session.value = null;
  clearApiCache(nuxtApp);
};

const notifyAuthChanged = () => {
  if (!import.meta.client) return;
  localStorage.setItem(AUTH_SYNC_KEY, String(Date.now()));
};

const getTranslator = () => {
  try {
    return useNuxtApp().$i18n?.t;
  } catch {
    return null;
  }
};

const translate = (key) => {
  const t = getTranslator();
  return t ? t(key) : key;
};

const translatedLoginFailure = () => ({
  status: false,
  message: translate('signIn.loginFailure')
});

const navigateSafely = (path, nuxtApp = null) => {
  const target = path || '/login';

  try {
    if (nuxtApp?.runWithContext) {
      return nuxtApp.runWithContext(() => navigateTo(target));
    }

    return navigateTo(target);
  } catch {
    if (import.meta.client && typeof window !== 'undefined') {
      window.location.assign(target);
    }

    return target;
  }
};

export const authLogin = async (email, password) => {
  const nuxtApp = (() => {
    try {
      return useNuxtApp();
    } catch {
      return null;
    }
  })();
  const auth = useAuth();
  const cookie = publicAuthCookie();
  let loginStatus = translatedLoginFailure();

  if (!auth) return loginStatus;

  try {
    const { error, pending } = await useClientApiFetch('/api/auth/login', {
      method: 'POST',
      body: {
        email,
        password
      },
      onResponseError({ response }) {
        if (response?._data?.message) {
          loginStatus.message = translate(`signIn.${response._data.message}`);
        }
      }
    });

    await pending.value;
    clearApiCache(nuxtApp);

    if (error.value) {
      clearLocalAuthState(auth, cookie, nuxtApp);
      return loginStatus;
    }

    await auth.fetchUser();

    if (!auth.loggedIn.value) {
      clearLocalAuthState(auth, cookie, nuxtApp);
      return loginStatus;
    }

    notifyAuthChanged();

    return {
      status: true,
      message: translate('signIn.loginSuccessful')
    };
  } catch (error) {
    clearLocalAuthState(auth, cookie, nuxtApp);

    const errorMessageKey = error?.data?.value?.message
      ? `signIn.${error.data.value.message}`
      : 'signIn.loginFailure';

    return {
      status: false,
      message: translate(errorMessageKey)
    };
  }
};

export const authRegister = async (
  name,
  surname,
  phoneNumber,
  email,
  country,
  state,
  city,
  gender,
  password,
  passwordConfirm
) => {
  await useClientApiFetch('/api/auth/register', {
    method: 'POST',
    body: {
      name,
      surname,
      phoneNumber,
      email,
      country,
      state,
      city,
      gender,
      password,
      passwordConfirm
    }
  });

  return await authLogin(email, password);
};

export const authLogout = async () => {
  const nuxtApp = (() => {
    try {
      return useNuxtApp();
    } catch {
      return null;
    }
  })();
  const auth = useAuth();
  const cookie = publicAuthCookie();
  const localePath = (() => {
    try {
      return useLocalePath();
    } catch {
      return (path) => path;
    }
  })();

  if (!auth) return navigateSafely(localePath('/login'), nuxtApp);

  try {
    await useClientApiFetch('/api/auth/logout', {
      method: 'POST'
    });
  } finally {
    clearLocalAuthState(auth, cookie, nuxtApp);
    notifyAuthChanged();
  }

  return navigateSafely(localePath('/login'), nuxtApp);
};

export const authRefresh = async () => {
  const nuxtApp = (() => {
    try {
      return useNuxtApp();
    } catch {
      return null;
    }
  })();
  const auth = useAuth();
  const cookie = publicAuthCookie();
  const localePath = (() => {
    try {
      return useLocalePath();
    } catch {
      return (path) => path;
    }
  })();

  if (!auth) return navigateSafely(localePath('/login'), nuxtApp);

  try {
    const { data, error, pending } = await useClientApiFetch('/api/auth/user', {
      method: 'GET'
    });

    await pending.value;

    if (error.value) {
      clearLocalAuthState(auth, cookie, nuxtApp);
      return navigateSafely(localePath('/login'), nuxtApp);
    }

    const safeUser = persistPublicAuthUser(
      data.value?.data || data.value,
      auth,
      cookie
    );
    if (!safeUser) {
      clearLocalAuthState(auth, cookie, nuxtApp);
      return navigateSafely(localePath('/login'), nuxtApp);
    }

    notifyAuthChanged();
    return true;
  } catch {
    clearLocalAuthState(auth, cookie, nuxtApp);
    return navigateSafely(localePath('/login'), nuxtApp);
  }
};
